
import { useState } from 'react';
import { CheckCircle, UserPlus, CheckCheck, Lock } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/use-toast';
import { toast as sonnerToast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';

export interface CreatorProps {
  id: string;
  name: string;
  avatar: string;
  subscribers: number;
  isSubscribed: boolean;
  onSubscribe: (id: string) => Promise<void>;
}

export const CreatorCard = ({
  id,
  name,
  avatar,
  subscribers,
  isSubscribed,
  onSubscribe
}: CreatorProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  
  const formatSubscribers = (count: number) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M abonnés`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K abonnés`;
    }
    return `${count} abonnés`;
  };
  
  const handleSubscribe = async () => {
    if (!user) {
      sonnerToast.error('Connexion requise', {
        description: 'Veuillez vous connecter pour vous abonner à ce créateur'
      });
      return;
    }
    
    setIsSubscribing(true);
    
    try {
      await onSubscribe(id);
    } catch (error) {
      console.error('Erreur lors de l\'abonnement:', error);
      sonnerToast.error('Erreur', {
        description: 'Une erreur est survenue lors de l\'abonnement. Veuillez réessayer.'
      });
    } finally {
      setIsSubscribing(false);
    }
  };

  // Mobile version
  if (isMobile) {
    return (
      <div className="bg-white dark:bg-youtube-dark border border-gray-200 dark:border-gray-800 rounded-xl p-3 transition-all duration-300 hover:shadow-md flex items-center gap-3">
        <div className="relative w-12 h-12 overflow-hidden rounded-full flex-shrink-0">
          {!isLoaded && (
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 animate-shimmer"></div>
          )}
          <img
            src={avatar}
            alt={name}
            className={`w-full h-full object-cover rounded-full ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setIsLoaded(true)}
          />
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-medium flex items-center gap-1">
            <span className="truncate block max-w-[120px]">{name}</span>
            <CheckCircle className="w-3 h-3 text-youtube-red flex-shrink-0" />
          </h3>
          
          <p className="text-youtube-gray-dark text-xs truncate max-w-[120px]">
            {formatSubscribers(subscribers)}
          </p>
        </div>
        
        <button
          onClick={handleSubscribe}
          disabled={isSubscribing}
          className={`py-1 px-3 rounded-full text-sm flex items-center justify-center gap-1 transition-all flex-shrink-0 ${
            isSubscribed
              ? 'bg-gray-100 dark:bg-gray-800 text-black dark:text-white'
              : 'bg-youtube-red text-white'
          } ${isSubscribing ? 'opacity-70 cursor-not-allowed' : ''}`}
        >
          {isSubscribing ? (
            <div className="w-3 h-3 border-2 border-t-transparent border-white rounded-full animate-spin"></div>
          ) : isSubscribed ? (
            <CheckCheck className="w-3 h-3" />
          ) : (
            <UserPlus className="w-3 h-3" />
          )}
          <span className="whitespace-nowrap">{isSubscribed ? 'Abonné' : 'S\'abonner'}</span>
        </button>
      </div>
    );
  }
  
  // Desktop version
  return (
    <div 
      className="bg-white dark:bg-youtube-dark border border-gray-200 dark:border-gray-800 rounded-xl p-4 transition-all duration-300 hover:shadow-xl flex flex-col items-center text-center animate-fade-in hover:scale-[1.03]"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative w-24 h-24 mb-4 overflow-hidden rounded-full transform transition-transform duration-500 hover:scale-105">
        {!isLoaded && (
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 animate-shimmer"></div>
        )}
        <img
          src={avatar}
          alt={name}
          className={`w-full h-full object-cover rounded-full transition-all duration-500 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setIsLoaded(true)}
        />
        {isLoaded && isHovered && (
          <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center rounded-full transition-opacity duration-300">
            {!user && <Lock className="w-8 h-8 text-white opacity-80" />}
          </div>
        )}
      </div>
      
      <h3 className="font-medium flex items-center gap-1 mb-1 justify-center">
        <span className="truncate max-w-[150px]">{name}</span>
        <CheckCircle className="w-3 h-3 text-youtube-red flex-shrink-0" />
      </h3>
      
      <p className="text-youtube-gray-dark text-sm mb-4 truncate max-w-[200px]">
        {formatSubscribers(subscribers)}
      </p>
      
      <button
        onClick={handleSubscribe}
        disabled={isSubscribing}
        className={`w-full py-2 px-4 rounded-full flex items-center justify-center gap-2 transition-all duration-300 ${
          isSubscribed
            ? 'bg-gray-100 dark:bg-gray-800 text-black dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700'
            : 'bg-youtube-red text-white hover:bg-red-600'
        } transform hover:translate-y-[-2px] active:translate-y-[0px] ${
          isSubscribing ? 'opacity-70 cursor-not-allowed' : ''
        }`}
      >
        {isSubscribing ? (
          <div className="w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin"></div>
        ) : isSubscribed ? (
          <>
            <CheckCheck className="w-4 h-4" />
            <span>Abonné</span>
          </>
        ) : (
          <>
            <UserPlus className="w-4 h-4" />
            <span>S'abonner</span>
          </>
        )}
      </button>
    </div>
  );
};

export default CreatorCard;
